import React, { useState, useEffect, useRef } from "react";
import avatar from "assets/img/avatars/avatar.webp";
import banner from "assets/img/profile/banner.png";
import Card from "components/card";
import { IoPencil, IoCheckmark, IoClose } from "react-icons/io5";
import { API_BASE_URL, buildApiUrl } from "utils/api";
import { getToken } from "utils/auth";
import axios from "axios";
import { usePermission } from "hooks/usePermission";

const Banner = ({ lead = {}, showToast, setCurrentLead }) => {
  const [isEditing, setIsEditing] = useState(false);
  const { can } = usePermission();
  const canUpdateLead = can("lead_management", "update");
  const [isSaving, setIsSaving] = useState(false);
  const [impression, setImpression] = useState("");
  const [editedData, setEditedData] = useState({});
  const [folderSearchTerm, setFolderSearchTerm] = useState("");
  const [matchedFolders, setMatchedFolders] = useState([]);
  const [remainingFolders, setRemainingFolders] = useState([]);
  const [folderPage, setFolderPage] = useState(1);
  const [isFoldersLoading, setIsFoldersLoading] = useState(false);
  const [showFolderDropdown, setShowFolderDropdown] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showPriorityDropdown, setShowPriorityDropdown] = useState(false);
  const [showSourceDropdown, setShowSourceDropdown] = useState(false);
  const folderDropdownRef = useRef(null);
  const statusDropdownRef = useRef(null);
  const priorityDropdownRef = useRef(null);
  const sourceDropdownRef = useRef(null);
  
  const editFieldSmallClassName =
    "w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm font-medium text-navy-700 transition hover:border-gray-300 focus:border-brand-500 focus:outline-none dark:border-white/10 dark:bg-navy-800 dark:text-white";

  // Initialize editing state when entering edit mode
  useEffect(() => {
    if (isEditing) {
      setEditedData({
        name: lead?.name || "",
        status: lead?.status || "",
        priority: lead?.priority || "",
        source: lead?.source || "",
        folderId: lead?.folderId?._id || "",
        impressions: lead?.impressions || "",
      });
      setFolderSearchTerm(lead?.folderId?.name || "");
    }
  }, [isEditing, lead]);

  // Sync impression with state
  useEffect(() => {
    if (lead?.impressions) {
      setImpression(lead.impressions);
    }
  }, [lead?.impressions]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        folderDropdownRef.current &&
        !folderDropdownRef.current.contains(event.target)
      ) {
        setShowFolderDropdown(false);
      }
      if (
        statusDropdownRef.current &&
        !statusDropdownRef.current.contains(event.target)
      ) {
        setShowStatusDropdown(false);
      }
      if (
        priorityDropdownRef.current &&
        !priorityDropdownRef.current.contains(event.target)
      ) {
        setShowPriorityDropdown(false);
      }
      if (
        sourceDropdownRef.current &&
        !sourceDropdownRef.current.contains(event.target)
      ) {
        setShowSourceDropdown(false);
      }
    };

    if (showFolderDropdown || showStatusDropdown || showPriorityDropdown || showSourceDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [showFolderDropdown, showStatusDropdown, showPriorityDropdown, showSourceDropdown]);

  const handleSave = async () => {
    if (!lead?._id) return;

    try {
      setIsSaving(true);
      const token = getToken();
      const formData = new FormData();

      // Add all fields except profile image
      if (editedData.name) formData.append("name", editedData.name);
      if (editedData.status) formData.append("status", editedData.status);
      if (editedData.priority) formData.append("priority", editedData.priority);
      if (editedData.source) formData.append("source", editedData.source);
      if (editedData.folderId) formData.append("folderId", editedData.folderId);
      if (editedData.impressions !== undefined) formData.append("impressions", editedData.impressions);

      // Add profile image if changed
      if (editedData.profileImage) {
        formData.append("profileImage", editedData.profileImage);
      }

      const response = await fetch(buildApiUrl(`/leads/${lead._id}`), {
        method: "PUT",
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to save changes");
      }

      const updatedLead = await response.json();
      setCurrentLead(updatedLead.data || updatedLead);
      setIsEditing(false);
      if (showToast) {
        showToast("Lead information updated successfully!", "success");
      }
    } catch (error) {
      console.error("Error saving lead:", error);
      if (showToast) {
        showToast(error.message || "Failed to save changes", "error");
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedData({});
    setImpression(lead?.impressions || "");
  };

  const handleImpressionChange = (value) => {
    setImpression(value);
    setEditedData((prev) => ({ ...prev, impressions: value }));
  };

  const handleNameChange = (value) => {
    setEditedData((prev) => ({ ...prev, name: value }));
  };

  const handleStatusChange = (value) => {
    setEditedData((prev) => ({ ...prev, status: value }));
  };

  const handlePriorityChange = (value) => {
    setEditedData((prev) => ({ ...prev, priority: value }));
  };

  const handleSourceChange = (value) => {
    setEditedData((prev) => ({ ...prev, source: value }));
  };

  const handleFolderChange = (folderId, folderName = "") => {
    setEditedData((prev) => ({ ...prev, folderId: folderId }));
    setFolderSearchTerm(folderName);
  };

  const searchFolders = async (searchTerm = "", page = 1) => {
    try {
      setIsFoldersLoading(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_BASE_URL}/folders/search`, {
        params: {
          q: searchTerm,
          page,
          type: "Lead",
          limit: 10,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data?.data) {
        const data = response.data.data;
        const matched = data.matched?.data || [];
        const remaining = data.remaining?.data || [];

        if (page === 1) {
          setMatchedFolders(matched);
          setRemainingFolders(remaining);
        } else {
          setRemainingFolders((prev) => [...prev, ...remaining]);
        }
        setFolderPage(page);
      }
    } catch (error) {
      console.error("Error searching folders:", error);
    } finally {
      setIsFoldersLoading(false);
    }
  };

  const handleFolderSearch = (value) => {
    setFolderSearchTerm(value);
    setFolderPage(1);
    if (value.trim() || !value) {
      // Always search, even on empty, to show current folder
      searchFolders(value);
    }
  };

  const handleFolderDropdownScroll = (e) => {
    const element = e.target;
    if (
      element.scrollHeight - element.scrollTop <= element.clientHeight + 50 &&
      !isFoldersLoading
    ) {
      searchFolders(folderSearchTerm, folderPage + 1);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const preview = URL.createObjectURL(file);
    setEditedData((prev) => ({
      ...prev,
      profileImage: file,
      profileImagePreview: preview,
    }));
  };

  return (
    <Card extra={"items-center w-full h-full overflow-hidden p-[16px] bg-cover"}>
      {/* Header with Edit/Save buttons */}
      <div className="mb-4 flex w-full items-center justify-between">
        <h5 className="text-base font-semibold text-navy-700 dark:text-white">
          Lead Profile
        </h5>
        <div className="flex gap-2">
          {!isEditing ? (
            canUpdateLead && (
              <button
                onClick={() => setIsEditing(true)}
                className="flex items-center gap-2 rounded-full bg-blue-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-600"
              >
                <IoPencil className="text-base" />
                Edit
              </button>
            )
          ) : (
            <>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="flex items-center gap-2 rounded-full bg-green-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-green-600 disabled:opacity-50"
              >
                <IoCheckmark className="text-base" />
                {isSaving ? "Saving..." : "Save"}
              </button>
              <button
                onClick={handleCancel}
                disabled={isSaving}
                className="flex items-center gap-2 rounded-full bg-gray-400 px-4 py-2 text-sm font-medium text-white transition hover:bg-gray-500 disabled:opacity-50"
              >
                <IoClose className="text-base" />
                Cancel
              </button>
            </>
          )}
        </div>
      </div>
      {/* Background and profile */}
      <div
        className="relative mt-1 flex h-32 w-full justify-center rounded-xl bg-cover"
        style={{ backgroundImage: `url(${banner})` }}
      >
        <div className="absolute -bottom-12 flex h-[87px] w-[87px] items-center justify-center rounded-full border-[4px] border-white bg-pink-400 dark:!border-navy-700">
          <img
            className="h-full w-full rounded-full object-cover"
            src={
              editedData.profileImagePreview ||
              editedData.profileImageUrl ||
              lead?.profileImage ||
              avatar
            }
            alt=""
          />
          {isEditing && (
            <label className="absolute bottom-0 right-0 cursor-pointer rounded-full bg-brand-500 p-2 text-white transition-colors hover:bg-brand-600">
              <IoPencil className="text-sm" />
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleImageChange}
              />
            </label>
          )}
        </div>
      </div>

      {/* Name and position */}
      <div className="mt-16 flex w-full flex-col items-center px-2 text-center">
        {isEditing ? (
          <input
            type="text"
            value={editedData.name || ""}
            onChange={(e) => handleNameChange(e.target.value)}
            className="w-full max-w-full rounded-lg border border-transparent bg-transparent px-2 py-1 text-center text-2xl font-bold text-navy-700 transition hover:border-gray-300 focus:border-brand-500 focus:outline-none dark:text-white sm:text-3xl"
            placeholder="Lead Name"
          />
        ) : (
          <h4 className="break-words text-center text-2xl font-bold text-navy-700 dark:text-white sm:text-3xl">
            {lead?.name || "Lead Name"}
          </h4>
        )}
      </div>

      {/* Post followers - 4 columns */}
      <div className="mb-3 mt-6 grid w-full grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-4 sm:gap-3 md:gap-4">
        <div className="flex min-w-0 flex-col items-center justify-center text-center">
          {isEditing ? (
            <div className="relative w-full" ref={statusDropdownRef}>
              <button
                onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                className={`${editFieldSmallClassName} text-left`}
              >
                {editedData.status || "Select Status"}
              </button>
              {showStatusDropdown && (
                <div className="absolute top-full left-0 right-0 z-50 mt-1 max-h-48 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg dark:border-navy-600 dark:bg-navy-800">
                  {["New", "Contacted", "Qualified", "Proposal", "Negotiation", "Closed", "Lost", "Converted", "Follow-Up"].map(
                    (status) => (
                      <div
                        key={status}
                        onClick={() => {
                          handleStatusChange(status);
                          setShowStatusDropdown(false);
                        }}
                        className="cursor-pointer px-3 py-2 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                      >
                        <p className="text-sm font-medium text-navy-700 dark:text-white">
                          {status}
                        </p>
                      </div>
                    )
                  )}
                </div>
              )}
            </div>
          ) : (
            <p className="break-words text-base font-bold text-navy-700 dark:text-white sm:text-xl">
              {lead?.status || "-"}
            </p>
          )}
          <p className="text-xs font-medium text-gray-600 dark:text-gray-400">Status</p>
        </div>
        <div className="flex min-w-0 flex-col items-center justify-center text-center">
          {isEditing ? (
            <div className="relative w-full" ref={priorityDropdownRef}>
              <button
                onClick={() => setShowPriorityDropdown(!showPriorityDropdown)}
                className={`${editFieldSmallClassName} text-left`}
              >
                {editedData.priority || "Select Priority"}
              </button>
              {showPriorityDropdown && (
                <div className="absolute top-full left-0 right-0 z-50 mt-1 max-h-48 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg dark:border-navy-600 dark:bg-navy-800">
                  {["Low", "Medium", "High", "Urgent"].map((priority) => (
                    <div
                      key={priority}
                      onClick={() => {
                        handlePriorityChange(priority);
                        setShowPriorityDropdown(false);
                      }}
                      className="cursor-pointer px-3 py-2 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                    >
                      <p className="text-sm font-medium text-navy-700 dark:text-white">
                        {priority}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <p className="break-words text-base font-bold text-navy-700 dark:text-white sm:text-xl">
              {lead?.priority || "-"}
            </p>
          )}
          <p className="text-xs font-medium text-gray-600 dark:text-gray-400">Priority</p>
        </div>
        <div className="flex min-w-0 flex-col items-center justify-center text-center">
          {isEditing ? (
            <div className="relative w-full" ref={sourceDropdownRef}>
              <button
                onClick={() => setShowSourceDropdown(!showSourceDropdown)}
                className={`${editFieldSmallClassName} text-left`}
              >
                {editedData.source || "Select Source"}
              </button>
              {showSourceDropdown && (
                <div className="absolute top-full left-0 right-0 z-50 mt-1 max-h-48 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg dark:border-navy-600 dark:bg-navy-800">
                  {["Website", "Referral", "Social Media", "Email", "Phone", "Direct", "Other", 'Ads', 'Walk-In'].map(
                    (source) => (
                      <div
                        key={source}
                        onClick={() => {
                          handleSourceChange(source);
                          setShowSourceDropdown(false);
                        }}
                        className="cursor-pointer px-3 py-2 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                      >
                        <p className="text-sm font-medium text-navy-700 dark:text-white">
                          {source}
                        </p>
                      </div>
                    )
                  )}
                </div>
              )}
            </div>
          ) : (
            <p className="break-words text-base font-bold text-navy-700 dark:text-white sm:text-xl">
              {lead?.source || "-"}
            </p>
          )}
          <p className="text-xs font-medium text-gray-600 dark:text-gray-400">Source</p>
        </div>

        {/* Folder Field */}
        <div className="flex min-w-0 flex-col items-center justify-center text-center">
          {isEditing ? (
            <div className="relative w-full" ref={folderDropdownRef}>
              <input
                type="text"
                value={folderSearchTerm}
                onChange={(e) => handleFolderSearch(e.target.value)}
                onFocus={() => {
                  setShowFolderDropdown(true);
                  if (
                    !folderSearchTerm ||
                    (!isFoldersLoading && matchedFolders.length === 0 && remainingFolders.length === 0)
                  ) {
                    handleFolderSearch(folderSearchTerm);
                  }
                }}
                placeholder={lead?.folderId?.name ? `${lead.folderId.name} (Current)` : "Search folder..."}
                className={editFieldSmallClassName}
              />
              {showFolderDropdown && (
                <div
                  className="absolute top-full left-0 right-0 z-50 mt-1 max-h-48 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg dark:border-navy-600 dark:bg-navy-800"
                  onScroll={handleFolderDropdownScroll}
                >
                  {isFoldersLoading && (
                    <div className="p-2 text-center text-sm text-gray-500">
                      Loading...
                    </div>
                  )}

                  {/* Current Folder */}
                  {lead?.folderId && (
                    <>
                      <div className="bg-gray-100 px-3 py-2 text-xs font-semibold text-gray-600 dark:bg-navy-700 dark:text-gray-300">
                        Current
                      </div>
                      <div
                        onClick={() => handleFolderChange(lead.folderId._id, lead.folderId.name)}
                        className={`flex min-h-[40px] items-center justify-between cursor-pointer border-b border-gray-100 px-3 py-2 hover:bg-blue-50 dark:border-navy-700 dark:hover:bg-blue-900/20 ${editedData.folderId === lead.folderId._id
                          ? "bg-blue-100 dark:bg-blue-900/10"
                          : ""
                          }`}
                      >
                        <p className="truncate text-sm font-medium text-navy-700 dark:text-white">
                          {lead.folderId.name}
                        </p>
                        {editedData.folderId === lead.folderId._id && (
                          <span className="ml-2 text-lg font-bold text-brand-500 flex-shrink-0">✓</span>
                        )}
                      </div>
                    </>
                  )}

                  {!isFoldersLoading && matchedFolders.length === 0 && remainingFolders.length === 0 && folderSearchTerm && (
                    <div className="p-2 text-center text-sm text-gray-500">
                      No folders found
                    </div>
                  )}

                  {/* Matched Folders */}
                  {matchedFolders.length > 0 && (
                    <>
                      <div className="bg-gray-100 px-3 py-2 text-xs font-semibold text-gray-600 dark:bg-navy-700 dark:text-gray-300">
                        Matched
                      </div>
                      {matchedFolders.map((folder) => (
                        <div
                          key={folder._id}
                          onClick={() => handleFolderChange(folder._id, folder.name)}
                          className={`flex min-h-[40px] items-center justify-between cursor-pointer px-3 py-2 hover:bg-blue-50 dark:hover:bg-blue-900/20 ${editedData.folderId === folder._id
                            ? "bg-blue-100 dark:bg-blue-900/10 border-l-2 border-brand-500"
                            : ""
                            }`}
                        >
                          <p className="truncate text-sm font-medium text-navy-700 dark:text-white">
                            {folder.name}
                          </p>
                          {editedData.folderId === folder._id && (
                            <span className="ml-2 text-lg font-bold text-brand-500 flex-shrink-0">✓</span>
                          )}
                        </div>
                      ))}
                    </>
                  )}

                  {/* Remaining Folders */}
                  {remainingFolders.length > 0 && (
                    <>
                      <div className="bg-gray-100 px-3 py-2 text-xs font-semibold text-gray-600 dark:bg-navy-700 dark:text-gray-300">
                        Other
                      </div>
                      {remainingFolders.map((folder) => (
                        <div
                          key={folder._id}
                          onClick={() => handleFolderChange(folder._id, folder.name)}
                          className={`flex min-h-[40px] items-center justify-between cursor-pointer px-3 py-2 hover:bg-blue-50 dark:hover:bg-blue-900/20 ${editedData.folderId === folder._id
                            ? "bg-blue-100 dark:bg-blue-900/10 border-l-2 border-brand-500"
                            : ""
                            }`}
                        >
                          <p className="truncate text-sm font-medium text-navy-700 dark:text-white">
                            {folder.name}
                          </p>
                          {editedData.folderId === folder._id && (
                            <span className="ml-2 text-lg font-bold text-brand-500 flex-shrink-0">✓</span>
                          )}
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>
          ) : (
            <p className="break-words text-base font-bold text-navy-700 dark:text-white sm:text-xl">
              {lead?.folderId?.name || "-"}
            </p>
          )}
          <p className="text-xs font-medium text-gray-600 dark:text-gray-400">Folder</p>
        </div>
      </div>

      <div className="mt-4 w-full px-1">
        <h5 className="mb-2 text-base font-semibold text-navy-700 dark:text-white">
          My Impression
        </h5>
        <textarea
          value={impression}
          onChange={(event) => handleImpressionChange(event.target.value)}
          placeholder="Write your impression about this lead..."
          className=" min-h-[100px] w-full resize-none rounded-xl border border-gray-200 bg-white p-3 text-sm text-navy-700 outline-none transition focus:border-brand-500 dark:border-white/10 dark:bg-navy-800 dark:text-white"
          disabled={!isEditing}
        />
      </div>
    </Card>
  );
};

export default Banner;
